<template>
  <div>
    <v-card>
      <v-card-title class="pa-0">
        <v-col cols="3" sm="3" class="pt-0">{{$t('ContentLibraryList.MediaLibrary')}}</v-col>

        <v-col cols="9" sm="9" class="d-flex justify-content-end">
          <v-text-field
            :disabled="loading"
            v-model="search"
            class="mt-1 ml-5"
            append-icon="mdi-magnify"
            :label="$t('GeneralTranslations.Search')"
            single-line
            hide-details
            style="max-width: 250px"
          ></v-text-field>

          <v-select
            :disabled="loading"
            v-model="filters.category"
            :items="categoryFilter"
            class="mt-1 ml-5"
            item-text="name"
            item-value="id"
            :label="$t('ContentLibraryList.FilterByCategory')"
            style="max-width: 250px"
          >
            <option value="">None</option>
          </v-select>

          <div class="ml-5" v-if='canManageContentCategories'>
            <v-btn :disabled="loading" color="primary" @click.native="addCategoryDialog = true" uppercase>{{$t('ContentLibraryList.ContentCategories')}}</v-btn>
            <AddContentCategory :value="addCategoryDialog" @close="addCategoryDialog = false"/>
          </div>

          <div class="ml-5">
            <v-btn :disabled="loading" color="primary" @click.native="addMediaDialog = true" uppercasae>{{$t('ContentLibraryList.AddMedia')}}</v-btn>
            <AddMedia :value="addMediaDialog" @close="addMediaDialog = false" />
          </div>
        </v-col>
      </v-card-title>
      <Loader v-if="loading"></Loader>
      <div v-else>
        <v-data-table
          :no-data-text="$t('ContentLibraryList.ThereAreCurrentlyNoMedia')"
          :headers="headers"
          :items="filteredContentItems"
          :search="search"
          :items-per-page="pagePer"
          :page="page"
          @click:row="handleClickContentItem"
          @update:page="updatePage"
          @update:items-per-page="updatePerPage"
        >
          <template v-slot:item.imageUrl="{ item }">
            <img class="content-img" :src="item.imageUrl" alt />
          </template>

          <template v-slot:item.name="{ item }">
            <b>{{item.name}}</b>
          </template> 
          
          <template v-slot:item.status="{ item }">
            <b>{{item.status  === 'Enabled'? $t('GeneralTranslations.Enabled'): $t('GeneralTranslations.Disabled')}}</b>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import AddMedia from '@/views/contentLibrary/AddMedia';
import AddContentCategory from '@/views/contentLibrary/AddContentCategory';
import { serializer } from '../../api/shared/serializer';
import moment from 'moment';
import Loader from "@/components/loader/Loader.vue";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";

export default {
  name: 'ContentLibraryList',
  components: { AddMedia, AddContentCategory, Loader },
  data() {
    return {
      loading: false,
      filters: {
        category: '',
      },
      addMediaDialog: false,
      addCategoryDialog: false,
      search: '',
      page: 1,
      pagePer: 10,
      headers: [
        {
          text: '',
          align: 'start',
          value: 'imageUrl',
          width: '40px',
          sortable: false
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Title'],
          align: "start",
          value: "name",
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Description'],
          align: 'start',
          sortable: false,
          value: 'description',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ContentLibraryList.Categories'],
          value: 'categoryNames',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ContentLibraryList.CreatedBy'],
          value: 'createdByStaffName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['ContentLibraryList.LastModified'],
          align: 'start',
          value: 'dateTimeUpdated',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Status'],
          align: 'start',
          value: 'isEnabled',
        },
      ],
    };
  },

  computed: {
    ...mapState({
      facility: (state) => state.facilityModule.selectedFacility,
      categories: (state) => state.contentLibraryModule.contentCategories,
      contentItems: (state) => serializer.transformFields(state.contentLibraryModule.contentItems, {
        isEnabled: isEnabled => isEnabled?'Enabled':'Disabled',
        dateTimeUpdated: date => date?moment(date.seconds * 1000).format(state.facilityModule.selectedFacility.dateFormat):'',
        description: description => description && description.length > 40 ?description.substring(0, 40) + '...':description,
      })
    }),
    filteredContentItems() {
      if(this.filters.category.length){
        return this.contentItems.filter((c) => c.categoryIds.includes(this.filters.category))
      }
      return this.contentItems
    },
    categoryFilter() {
      return [
        {
          id: '',
          name: 'All',
        },
        ...this.categories,
      ];
    },
    currentUser() {
      return this.$store.state.authModule.user
    },
    canManageContentCategories(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.MediaLibrary_List_ManageContentCategories)
    }
  },

  created() {
    this.loading = true;
    this.$store.dispatch("getFacility");
    this.$store.dispatch('getContentItems');
    this.$store.dispatch("getContentCategories");
    this.pagePer = this.$route.params.pagePer;
    this.page = this.$route.params.page;
  },

  watch:{
    contentItems(items){
      this.loading = false;
    }
  },

  methods: {
    handleClickContentItem(row) {
      this.$router.push({
        name: `ContentLibraryView`,
        params: {
          id: row.id,
          page: this.page,
          pagePer: this.pagePer
        },
      })
    },
    updatePage(page) {
      this.page = page;
    },
    updatePerPage(pagePer) {
      this.pagePer = pagePer;
    }
  },
};
</script>

<style scoped>
.content-img {
  width: 60px;
  border-radius: 0px;
}

::v-deep tr {
  height: 80px !important;
} 
</style>
